import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Alert from "../../../Common/Alert";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import {
  ChartOfAccountStyles,
  formatDate,
  money,
  num,
} from "../../Accounts/Chart-Of-Account/ChartOfAccountShared";
import {
  FinancialStatementStyles,
  PresetChips,
  amount,
  endOfLastMonth,
  endOfLastYear,
  printStatement,
  today,
} from "./FinancialStatementShared";
import {
  AgeBadge,
  SbStyles,
  csvCell,
  daysBetween,
  decode,
  escapeHtml,
} from "./SupplierBalanceReport";

// Distributor Receivable and Distributor Payable are the same report read
// from opposite sides of the distributor's ledger.
const MODES = {
  receivable: {
    title: "Distributor Receivable",
    endpoint: "/distributor-receivable-report",
    balance: "Receivable",
    subtitle: "What each distributor owes",
    formula: "opening due + debits - credits",
    totalIcon: "fa-arrow-circle-down",
    empty: "No distributor owes anything",
    only: "Receivable only",
    opposite: "Held as Advance",
    oppositeIcon: "fa-forward",
    oppositeTip: "In credit: held as an advance",
    oppositeNote: "Amounts in brackets are held as an advance.",
    opening: "Opening Due",
    openingRow: "Opening due (Previous Due on the distributor)",
    movement: (t) => `+ ${money(t.debit)} debit · − ${money(t.credit)} credit`,
    file: "distributor-receivable",
  },
  payable: {
    title: "Distributor Payable",
    endpoint: "/distributor-payable-report",
    balance: "Payable",
    subtitle: "What we owe each distributor",
    formula: "credits - debits - opening due",
    totalIcon: "fa-arrow-circle-up",
    empty: "Nothing is owed to any distributor",
    only: "Payable only",
    opposite: "Owed to Us",
    oppositeIcon: "fa-arrow-circle-down",
    oppositeTip: "The distributor owes us: a receivable",
    oppositeNote: "Amounts in brackets are owed to us.",
    opening: "Opening",
    openingRow: "Opening balance (Previous Due on the distributor, reversed)",
    movement: (t) => `+ ${money(t.credit)} credit · − ${money(t.debit)} debit`,
    file: "distributor-payable",
  },
};

const SORTS = [
  ["balance", "Largest balance"],
  ["name", "Distributor name"],
  ["last_date", "Most recent entry"],
  ["depot", "Depot"],
];

const BUSINESS_TYPES = {
  retail: "Retail",
  commission: "Commission",
  institute: "Institute",
  wholeSale: "Wholesale",
  temporary: "Temporary",
  rdso: "RDSO",
  ho: "Head Office",
};

const typeLabel = (type) => BUSINESS_TYPES[type] || type || "";

const DrStyles = () => (
  <style>
    {`
      .dr-status {
        display: inline-block;
        margin-left: 6px;
        padding: 0 6px;
        border-radius: 8px;
        font-size: 0.62rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        vertical-align: middle;
        background: #f2f3f9;
        color: #8b90a5;
      }
      .dr-status.is-resign { background: #fdecec; color: #e42320; }
      .dr-opening-row td { background: #f7f8fc; font-style: italic; color: #52566b; }
      .dr-none { font-size: 0.7rem; color: #b6bacb; }
    `}
  </style>
);

// A distributor's opening due and entries up to the statement date, loaded
// when the row is opened.
const DistributorLedger = ({ ledger, config }) => {
  if (!ledger || ledger.loading) {
    return (
      <div className="sb-ledger-note">
        <i className="fa fa-spinner fa-spin mg-r-6"></i>Loading entries...
      </div>
    );
  }
  if (ledger.error) {
    return <div className="sb-ledger-note sb-neg">{ledger.error}</div>;
  }
  const hasOpening = Math.abs(num(ledger.opening)) >= 0.005;
  if (!ledger.entries.length && !hasOpening) {
    return <div className="sb-ledger-note">No entries.</div>;
  }
  return (
    <table className="sb-ledger">
      <thead>
        <tr>
          <th>Date</th>
          <th>Voucher</th>
          <th>Account</th>
          <th>Remarks</th>
          <th className="coa-num">Debit</th>
          <th className="coa-num">Credit</th>
          <th className="coa-num">{config.balance}</th>
        </tr>
      </thead>
      <tbody>
        {hasOpening && (
          <tr className="dr-opening-row">
            <td colSpan={6}>{config.openingRow}</td>
            <td className={`coa-num ${num(ledger.opening) < 0 ? "sb-neg" : ""}`} style={{ fontWeight: 600 }}>
              {amount(ledger.opening)}
            </td>
          </tr>
        )}
        {ledger.entries.map((entry) => (
          <tr key={entry.id}>
            <td style={{ whiteSpace: "nowrap" }}>{formatDate(entry.date)}</td>
            <td style={{ whiteSpace: "nowrap" }}>
              <Link to={`/journal-voucher-details/${entry.voucher_id}`}>
                {entry.voucher_no}
              </Link>
              <span className="sb-sub" style={{ textTransform: "capitalize" }}>
                {entry.voucher_type}
              </span>
            </td>
            <td>
              {entry.account_code} - {entry.account_name}
            </td>
            <td>{entry.remarks}</td>
            <td className="coa-num">{num(entry.debit) ? money(entry.debit) : ""}</td>
            <td className="coa-num">{num(entry.credit) ? money(entry.credit) : ""}</td>
            <td
              className={`coa-num ${num(entry.balance) < 0 ? "sb-neg" : ""}`}
              style={{ fontWeight: 600 }}
            >
              {amount(entry.balance)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const DistributorBalanceReport = ({ mode }) => {
  const config = MODES[mode];
  const [searchParams, setSearchParams] = useSearchParams();
  const [asOf, setAsOf] = useState(searchParams.get("as_of") || today());
  const [showAll, setShowAll] = useState(searchParams.get("show") === "all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("balance");
  const [depot, setDepot] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [open, setOpen] = useState({});
  const [ledgers, setLedgers] = useState({});

  const fetchData = async (date = asOf, all = showAll) => {
    setLoading(true);
    setAlert(null);
    setOpen({});
    setLedgers({});

    const query = { as_of: date };
    if (all) query.show = "all";
    setSearchParams(query, { replace: true });

    try {
      const res = await Http.get(config.endpoint, { params: query });
      setData(res.data);
    } catch (error) {
      console.error("Fetch error:", error);
      setData(null);
      setAlert({
        message:
          error.response?.data?.message ||
          "Could not load the report. Please try again.",
        type: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const applyDate = (date) => {
    setAsOf(date);
    fetchData(date);
  };

  const toggleShowAll = (value) => {
    setShowAll(value);
    fetchData(asOf, value);
  };

  const toggleDistributor = (row) => {
    const isOpen = !open[row.id];
    setOpen((values) => ({ ...values, [row.id]: isOpen }));
    if (!isOpen || ledgers[row.id]) return;

    setLedgers((values) => ({ ...values, [row.id]: { loading: true } }));
    Http.get("/distributor-balance-ledger", {
      params: { report: mode, distributor_id: row.id, as_of: data.as_of },
    })
      .then((res) =>
        setLedgers((values) => ({
          ...values,
          [row.id]: { opening: res.data.opening, entries: res.data.entries || [] },
        })),
      )
      .catch((error) =>
        setLedgers((values) => ({
          ...values,
          [row.id]: {
            error:
              error.response?.data?.message || "Could not load the entries.",
          },
        })),
      );
  };

  // Filter choices come from the rows actually on the report.
  const depots = useMemo(
    () =>
      data
        ? [...new Set(data.rows.map((row) => row.depot || ""))].sort((a, b) =>
            a.localeCompare(b),
          )
        : [],
    [data],
  );
  const businessTypes = useMemo(
    () => (data ? [...new Set(data.rows.map((row) => row.business_type).filter(Boolean))] : []),
    [data],
  );

  const rows = useMemo(() => {
    const list = data ? [...data.rows] : [];
    const query = search.trim().toLowerCase();
    const filtered = list.filter(
      (row) =>
        (!depot || (row.depot || "") === depot) &&
        (!businessType || row.business_type === businessType) &&
        (!query ||
          decode(`${row.code || ""} ${row.name} ${row.contact_person || ""} ${row.contact_phone || ""}`)
            .toLowerCase()
            .includes(query)),
    );
    const compare = {
      balance: (a, b) => num(b.balance) - num(a.balance),
      name: (a, b) => decode(a.name).localeCompare(decode(b.name)),
      last_date: (a, b) => (b.last_date || "").localeCompare(a.last_date || ""),
      depot: (a, b) =>
        (a.depot || "").localeCompare(b.depot || "") || num(b.balance) - num(a.balance),
    }[sort];
    return filtered.sort(compare);
  }, [data, search, sort, depot, businessType]);

  const totals = useMemo(
    () => ({
      opening: rows.reduce((sum, row) => sum + num(row.opening), 0),
      debit: rows.reduce((sum, row) => sum + num(row.debit), 0),
      credit: rows.reduce((sum, row) => sum + num(row.credit), 0),
      balance: rows.reduce((sum, row) => sum + num(row.balance), 0),
    }),
    [rows],
  );

  const outstanding = data ? num(data.totals.outstanding) : 0;
  const owing = data ? data.rows.filter((row) => num(row.balance) > 0.004) : [];
  const largest = owing.length
    ? owing.reduce((top, row) => (num(row.balance) > num(top.balance) ? row : top))
    : null;
  // Owing and quiet: no entry for 90 days, or never an entry beyond the opening.
  const idle = data
    ? owing.filter(
        (row) => !row.last_date || daysBetween(row.last_date, data.as_of) > 90,
      )
    : [];
  const neverPosted = idle.filter((row) => !row.last_date).length;

  const share = (value) =>
    outstanding > 0 && num(value) > 0 ? (num(value) * 100) / outstanding : 0;

  const scopeLabel =
    data && data.accounts.length
      ? data.accounts.map((a) => `${a.code} - ${a.name}`).join(", ")
      : "all accounts";

  const filterLabel = [depot && `Depot: ${depot}`, businessType && `Type: ${typeLabel(businessType)}`]
    .filter(Boolean)
    .join(" · ");

  const exportCsv = () => {
    const lines = [
      [config.title],
      [`As at ${data.as_of}${data.show === "all" ? " (all distributors)" : ""}${filterLabel ? ` - ${filterLabel}` : ""}`],
      [],
      ["Sl", "Code", "Distributor", "Depot", "Business Type", "Status", "Contact", config.opening, "Debit", "Credit", config.balance, "Share %", "Entries", "Last Entry"],
      ...rows.map((row, i) => [
        i + 1,
        row.code,
        decode(row.name),
        row.depot,
        typeLabel(row.business_type),
        row.status,
        [row.contact_person, row.contact_phone].filter(Boolean).join(" "),
        num(row.opening).toFixed(2),
        num(row.debit).toFixed(2),
        num(row.credit).toFixed(2),
        num(row.balance).toFixed(2),
        share(row.balance).toFixed(2),
        row.entries,
        row.last_date,
      ]),
      [],
      ["", "", "Total", "", "", "", "", totals.opening.toFixed(2), totals.debit.toFixed(2), totals.credit.toFixed(2), totals.balance.toFixed(2)],
    ];
    const csv = lines.map((line) => line.map(csvCell).join(",")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${config.file}_${data.as_of}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const headHtml = `
      <th>Sl</th><th>Code</th><th>Distributor</th><th>Depot</th>
      <th class="num">${config.opening}</th><th class="num">Debit</th><th class="num">Credit</th>
      <th class="num">${config.balance}</th><th class="num">Share</th><th>Last Entry</th>`;
    const bodyHtml =
      rows
        .map(
          (row, i) => `
        <tr>
          <td>${i + 1}</td>
          <td class="code">${escapeHtml(row.code)}</td>
          <td>${escapeHtml(decode(row.name))}</td>
          <td>${escapeHtml(row.depot)}</td>
          <td class="num">${amount(row.opening)}</td>
          <td class="num">${money(row.debit)}</td>
          <td class="num">${money(row.credit)}</td>
          <td class="num">${amount(row.balance)}</td>
          <td class="num">${share(row.balance) ? `${share(row.balance).toFixed(1)}%` : ""}</td>
          <td>${row.last_date ? formatDate(row.last_date) : "–"}</td>
        </tr>`,
        )
        .join("") +
      `<tr class="grand">
        <td colspan="4">Total</td>
        <td class="num">${amount(totals.opening)}</td>
        <td class="num">${money(totals.debit)}</td>
        <td class="num">${money(totals.credit)}</td>
        <td class="num">${amount(totals.balance)}</td>
        <td colspan="2"></td>
      </tr>`;

    printStatement({
      title: config.title,
      subtitle: `As at ${formatDate(data.as_of)}${
        data.show === "all" ? " &middot; All distributors" : ""
      }${filterLabel ? ` &middot; ${escapeHtml(filterLabel)}` : ""} &middot; Amounts in Taka`,
      headHtml,
      bodyHtml,
      note: `${config.balance} = ${config.formula} on the distributor's postings on ${scopeLabel} up to the date.${
        data.show === "all" ? ` ${config.oppositeNote}` : ""
      }`,
    });
  };

  const stat = (label, icon, value, sub, className = "") => (
    <div className="col-md-3 mg-b-10">
      <div className={`coa-stat ${className}`}>
        <i className={`fa ${icon} coa-stat-icon`}></i>
        <span className="coa-stat-label">{label}</span>
        <span className="coa-stat-value">{value}</span>
        <span className="coa-stat-sub">{sub}</span>
      </div>
    </div>
  );

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <FinancialStatementStyles />
        <SbStyles />
        <DrStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">{config.title}</h5>
            <p className="text-muted mb-0 tx-12">
              {config.subtitle}, as at {formatDate(data?.as_of || asOf)}
            </p>
          </div>
          <div className="d-flex gap-2 coa-no-print">
            <Link to="/reports" className="btn btn-outline-secondary">
              <i className="fa fa-arrow-left mg-r-6"></i>Reports
            </Link>
            <button
              className="btn btn-outline-secondary"
              onClick={() => fetchData()}
              title="Reload data"
              disabled={loading}
            >
              <i className="fa fa-refresh"></i>
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={exportCsv}
              disabled={loading || !rows.length}
            >
              <i className="fa fa-file-excel-o mg-r-6"></i>Export CSV
            </button>
            <button
              className="btn btn-primary"
              onClick={handlePrint}
              disabled={loading || !data}
            >
              <i className="fa fa-print mg-r-6"></i>Print
            </button>
          </div>
        </div>

        <div className="am-pagebody">
          <Alert alert={alert} />

          <div className="coa-panel coa-no-print">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-calendar-o me-2"></i>Statement Date
              </span>
              <span className="coa-head-note">
                Opening balance plus every distributor posting up to the date, on {scopeLabel}
              </span>
            </div>
            <div className="coa-panel-body">
              <div className="row align-items-start">
                <div className="col-lg-4">
                  <label>As at</label>
                  <input
                    type="date"
                    className="form-control"
                    value={asOf}
                    onChange={(e) => setAsOf(e.target.value)}
                  />
                  <PresetChips
                    presets={[
                      ["Today", today()],
                      ["Last month end", endOfLastMonth()],
                      ["Last year end", endOfLastYear()],
                    ]}
                    isActive={([, value]) => asOf === value}
                    onPick={([, value]) => applyDate(value)}
                  />
                </div>
                <div className="col-lg-2 mg-t-10 mg-lg-t-0">
                  <label>&nbsp;</label>
                  <button
                    className="btn btn-primary"
                    onClick={() => fetchData()}
                    disabled={loading || !asOf}
                  >
                    <i
                      className={`fa ${loading ? "fa-spinner fa-spin" : "fa-search"} mg-r-6`}
                    ></i>
                    {loading ? "Loading..." : "Apply"}
                  </button>
                </div>
                <div className="col-lg-4 mg-t-10 mg-lg-t-0">
                  <label>Distributors</label>
                  <PresetChips
                    presets={[
                      [config.only, false],
                      ["All distributors", true],
                    ]}
                    isActive={([, value]) => showAll === value}
                    onPick={([, value]) => toggleShowAll(value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {data && (
            <div className="row mg-b-20">
              {stat(
                `Total ${config.balance}`,
                config.totalIcon,
                money(outstanding),
                `${owing.length} distributor${owing.length === 1 ? "" : "s"}`,
              )}
              {data.show === "all"
                ? stat(
                    config.opposite,
                    config.oppositeIcon,
                    money(data.totals.opposite),
                    `Net ${amount(data.totals.balance)} ${config.balance.toLowerCase()}`,
                  )
                : stat(
                    config.opening,
                    "fa-history",
                    amount(data.totals.opening),
                    config.movement(data.totals),
                  )}
              {stat(
                `Largest ${config.balance}`,
                "fa-arrow-up",
                largest ? money(largest.balance) : "–",
                largest
                  ? `${decode(largest.name)} · ${share(largest.balance).toFixed(1)}% of total`
                  : "–",
              )}
              {stat(
                "Idle Over 90 Days",
                "fa-clock-o",
                idle.length,
                idle.length
                  ? neverPosted
                    ? `${neverPosted} with no entry beyond the opening`
                    : "No entry for more than 90 days"
                  : "Every balance has recent activity",
                idle.length ? "is-alert" : "is-ok",
              )}
            </div>
          )}

          <div className="coa-panel">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-table me-2"></i>Distributor Balances
                <span className="coa-head-note mg-l-10">
                  Click a distributor to see its entries
                </span>
              </span>
              {data && data.rows.length > 0 && (
                <span className="sb-tools coa-no-print">
                  {depots.length > 1 && (
                    <select
                      className="form-control"
                      value={depot}
                      onChange={(e) => setDepot(e.target.value)}
                      aria-label="Depot"
                    >
                      <option value="">All depots</option>
                      {depots.map((name) => (
                        <option key={name || "none"} value={name}>
                          {name || "No depot"}
                        </option>
                      ))}
                    </select>
                  )}
                  {businessTypes.length > 1 && (
                    <select
                      className="form-control"
                      value={businessType}
                      onChange={(e) => setBusinessType(e.target.value)}
                      aria-label="Business type"
                    >
                      <option value="">All types</option>
                      {businessTypes.map((type) => (
                        <option key={type} value={type}>
                          {typeLabel(type)}
                        </option>
                      ))}
                    </select>
                  )}
                  <select
                    className="form-control"
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    aria-label="Sort by"
                  >
                    {SORTS.map(([value, label]) => (
                      <option key={value} value={value}>
                        Sort: {label}
                      </option>
                    ))}
                  </select>
                  <div className="sb-search">
                    <i className="fa fa-search" aria-hidden="true"></i>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search distributor"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      aria-label="Search distributor"
                    />
                  </div>
                </span>
              )}
            </div>

            {loading && !data ? (
              <div className="pd-y-10">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="fs-skeleton"></div>
                ))}
              </div>
            ) : !data ? (
              <div className="coa-empty">
                <i className="fa fa-exclamation-circle"></i>
                The report could not be loaded.
              </div>
            ) : !rows.length ? (
              <div className="coa-empty">
                <i className="fa fa-check-circle-o"></i>
                {data.rows.length
                  ? "No distributors match the filters."
                  : `${config.empty} as at ${formatDate(data.as_of)}.`}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="sb-table">
                  <thead>
                    <tr>
                      <th>Sl</th>
                      <th>Code</th>
                      <th>Distributor</th>
                      <th>Depot</th>
                      <th className="coa-num">{config.opening}</th>
                      <th className="coa-num">Debit</th>
                      <th className="coa-num">Credit</th>
                      <th className="coa-num">{config.balance}</th>
                      <th>Share of Total</th>
                      <th>Last Entry</th>
                    </tr>
                  </thead>
                  <tbody style={{ opacity: loading ? 0.5 : 1 }}>
                    {rows.map((row, i) => {
                      const balance = num(row.balance);
                      const isOpen = !!open[row.id];
                      return (
                        <React.Fragment key={row.id}>
                          <tr className={`sb-row${isOpen ? " is-open" : ""}`}>
                            <td className="sb-sl">{i + 1}</td>
                            <td>
                              {row.code && (
                                <span className="coa-code">{row.code}</span>
                              )}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="sb-name-btn"
                                onClick={() => toggleDistributor(row)}
                                aria-expanded={isOpen}
                              >
                                <i
                                  className={`fa fa-caret-${isOpen ? "down" : "right"} mg-r-6`}
                                  aria-hidden="true"
                                ></i>
                                {decode(row.name)}
                              </button>
                              {row.status && row.status !== "active" && (
                                <span className={`dr-status is-${row.status}`}>
                                  {row.status === "resign" ? "Resigned" : row.status}
                                </span>
                              )}
                              <Link
                                to={`/distributor-details/${row.id}`}
                                className="sb-vendor-link"
                                title="Open the distributor"
                              >
                                <i className="fa fa-external-link"></i>
                              </Link>
                              {(row.contact_person || row.contact_phone) && (
                                <span className="sb-sub">
                                  {[row.contact_person, row.contact_phone]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </span>
                              )}
                            </td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              {row.depot || <span className="dr-none">No depot</span>}
                              {row.business_type && (
                                <span className="sb-sub">{typeLabel(row.business_type)}</span>
                              )}
                            </td>
                            <td className="coa-num">{amount(row.opening)}</td>
                            <td className="coa-num">{money(row.debit)}</td>
                            <td className="coa-num">{money(row.credit)}</td>
                            <td
                              className={`coa-num ${
                                balance < -0.004
                                  ? "sb-neg"
                                  : Math.abs(balance) <= 0.004
                                    ? "sb-settled"
                                    : ""
                              }`}
                              style={{ fontWeight: 700, color: balance > 0.004 ? "#2a2962" : undefined }}
                              title={balance < -0.004 ? config.oppositeTip : undefined}
                            >
                              {amount(row.balance)}
                            </td>
                            <td>
                              {share(row.balance) > 0 && (
                                <div className="sb-share">
                                  <div className="sb-share-bar">
                                    <div
                                      className="sb-share-fill"
                                      style={{ width: `${share(row.balance)}%` }}
                                    ></div>
                                  </div>
                                  <span className="sb-share-pct">
                                    {share(row.balance).toFixed(1)}%
                                  </span>
                                </div>
                              )}
                            </td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              {row.last_date ? (
                                <>
                                  {formatDate(row.last_date)}
                                  <span className="sb-sub">
                                    <AgeBadge lastDate={row.last_date} asOf={data.as_of} />{" "}
                                    {row.entries} entr{row.entries === 1 ? "y" : "ies"}
                                  </span>
                                </>
                              ) : (
                                <span className="dr-none">Opening only</span>
                              )}
                            </td>
                          </tr>
                          {isOpen && (
                            <tr>
                              <td colSpan={10} className="sb-ledger-cell">
                                <DistributorLedger ledger={ledgers[row.id]} config={config} />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={4} style={{ textAlign: "right" }}>
                        Total{filterLabel ? ` (${filterLabel})` : ""}
                      </td>
                      <td className="coa-num">{amount(totals.opening)}</td>
                      <td className="coa-num">{money(totals.debit)}</td>
                      <td className="coa-num">{money(totals.credit)}</td>
                      <td className="coa-num">{amount(totals.balance)}</td>
                      <td colSpan={2}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DistributorBalanceReport;
